"use client"

import { useState, useEffect, useMemo, useRef, useCallback } from "react"
import Link from "next/link"
import Head from "next/head"
import { ArrowLeft, Clock, Calendar, User } from "lucide-react"
import { useQuery } from "@tanstack/react-query"
import { Navbar, Footer, AnimateOnScroll } from "@/components/index"
import { TableOfContents } from "@/components/blog/TableOfContents"
import { BlogDetailSkeleton } from "@/components/blog/BlogDetailSkeleton"
import { BlogSection, BlogPost } from "@/types/blog"
import { formatDate } from "@/lib/utils"
import { BlogContentRenderer } from "@/components/blog/BlogContentRenderer"
import { getPostBySlug } from "@/lib/supabase/queries"

// AEO-optimized JSON-LD structured data for blog post
// Includes semantic triples, author attribution, and content cluster signals
function generateArticleStructuredData(post: BlogPost) {
  const wordCount = post.content.split(/\s+/).length
  const readingTime = post.readingTime || Math.ceil(wordCount / 200)

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BlogPosting",
        "@id": `https://specledger.io/blog/?slug=${post.slug}#article`,
        headline: post.title,
        description: post.excerpt,
        datePublished: new Date(post.publishedAt).toISOString(),
        dateModified: post.updatedAt
          ? new Date(post.updatedAt).toISOString()
          : new Date(post.publishedAt).toISOString(),
        author: {
          "@type": "Person",
          name: post.author || "Specledger Team",
          url: "https://specledger.io/about/",
        },
        publisher: {
          "@type": "Organization",
          "@id": "https://specledger.io/#organization",
          name: "Specledger",
          logo: {
            "@type": "ImageObject",
            url: "https://specledger.io/logo.png",
          },
        },
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": `https://specledger.io/blog/?slug=${post.slug}`,
        },
        isPartOf: {
          "@id": "https://specledger.io/blog/#blog",
        },
        keywords: post.tags.join(", "),
        wordCount,
        timeRequired: `PT${readingTime}M`,
        articleSection: post.tags[0] || "Engineering",
        inLanguage: "en-US",
      },
      // AEO: BreadcrumbList for content cluster navigation
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://specledger.io",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Blog",
            item: "https://specledger.io/blog/",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: post.title,
            item: `https://specledger.io/blog/?slug=${post.slug}`,
          },
        ],
      },
    ],
  }
}

// Check if content is HTML
function isHtmlContent(content: string): boolean {
  const trimmed = content.trim()
  return trimmed.startsWith("<") || trimmed.includes("<div") || trimmed.includes("<p>") || trimmed.includes("<h")
}

// Extract sections from HTML content
function extractSectionsFromHtml(content: string): BlogSection[] {
  const sections: BlogSection[] = []
  const seenIds = new Set<string>()

  const headingRegex = /<h([1-3])([^>]*)>([\s\S]*?)<\/h\1>/gi

  let match
  while ((match = headingRegex.exec(content)) !== null) {
    const level = parseInt(match[1])
    const attrs = match[2]
    const title = match[3].replace(/<[^>]+>/g, '').trim()

    if (!title) continue

    const idMatch = attrs.match(/id="([^"]+)"/)
    const id = idMatch
      ? idMatch[1]
      : title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')

    if (seenIds.has(id)) continue
    seenIds.add(id)

    if (level === 1 || level === 2 || level === 3) {
      sections.push({ id, title, level: level as 1 | 2 | 3 })
    }
  }

  return sections
}

// Extract sections from markdown content
function extractSectionsFromMarkdown(content: string): BlogSection[] {
  const sections: BlogSection[] = []
  const lines = content.split('\n')

  for (const line of lines) {
    const h3Match = line.match(/^### (.+)$/)
    const h2Match = line.match(/^## (.+)$/)
    const h1Match = line.match(/^# ([^#].*)$/)
    const boldMatch = line.match(/^\*\*([^*]+)\*\*\s*$/)

    if (h3Match) {
      const title = h3Match[1].trim()
      const id = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
      sections.push({ id, title, level: 3 as const })
    } else if (h2Match) {
      const title = h2Match[1].trim()
      const id = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
      sections.push({ id, title, level: 2 as const })
    } else if (h1Match) {
      const title = h1Match[1].trim()
      const id = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
      if (sections.length > 0) {
        sections.push({ id, title, level: 1 as const })
      }
    } else if (boldMatch) {
      const title = boldMatch[1].trim()
      const id = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
      sections.push({ id, title, level: 2 as const })
    }
  }

  return sections
}

// Extract sections from content (auto-detect HTML or markdown)
function extractSectionsFromContent(content: string): BlogSection[] {
  if (isHtmlContent(content)) {
    return extractSectionsFromHtml(content)
  }
  return extractSectionsFromMarkdown(content)
}

interface BlogDetailClientProps {
  slug: string
}

export function BlogDetailClient({ slug }: BlogDetailClientProps) {
  const [activeSection, setActiveSection] = useState<string>("")
  const isClickScrolling = useRef(false)
  const hasSetInitialSection = useRef(false)

  const {
    data: post,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["blog-post", slug],
    queryFn: () => getPostBySlug(slug),
    enabled: !!slug,
  })

  // Use provided sections or auto-extract from content
  const sections = useMemo(() => {
    if (!post) return []
    if (post.sections && post.sections.length > 0) {
      return post.sections
    }
    return extractSectionsFromContent(post.content)
  }, [post])

  // Handle section click - disable observer temporarily
  const handleSectionClick = useCallback((sectionId: string) => {
    isClickScrolling.current = true
    setActiveSection(sectionId)

    setTimeout(() => {
      isClickScrolling.current = false
    }, 1000)
  }, [])

  useEffect(() => {
    if (sections.length === 0) return

    // Reset initial section flag when sections change (new post)
    hasSetInitialSection.current = false

    let retryCount = 0
    const maxRetries = 10
    const HEADER_OFFSET = 120

    const handleScroll = () => {
      if (isClickScrolling.current) return

      // Find the section that is currently at or just past the top of viewport
      let currentSection = sections[0]?.id || ""

      for (const section of sections) {
        const element = document.getElementById(section.id)
        if (element) {
          const rect = element.getBoundingClientRect()
          // If the element's top is above the threshold, this section is active
          if (rect.top <= HEADER_OFFSET + 20) {
            currentSection = section.id
          } else {
            // Stop checking once we find a section below the threshold
            break
          }
        }
      }

      if (currentSection) {
        setActiveSection(currentSection)
      }
    }

    const setupScrollListener = () => {
      // Check if elements exist
      const foundElements = sections.filter(
        (section) => document.getElementById(section.id)
      )

      // If no elements found and we haven't exceeded retries, try again
      if (foundElements.length === 0 && retryCount < maxRetries) {
        retryCount++
        setTimeout(setupScrollListener, 100)
        return
      }

      // Set first section as active on initial load
      if (!hasSetInitialSection.current && sections.length > 0) {
        hasSetInitialSection.current = true
        handleScroll() // Run once to set initial active section
      }

      // Add scroll listener
      window.addEventListener("scroll", handleScroll, { passive: true })
    }

    // Start setup with initial delay for DOM to render
    const timeoutId = setTimeout(setupScrollListener, 200)

    return () => {
      clearTimeout(timeoutId)
      window.removeEventListener("scroll", handleScroll)
    }
  }, [sections])

  // Generate structured data for SEO - must be before early returns to follow Rules of Hooks
  const structuredData = useMemo(() => {
    if (!post) return null
    return generateArticleStructuredData(post)
  }, [post])

  if (isLoading) {
    return <BlogDetailSkeleton />
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-bg">
        <Navbar />
        <div className="flex items-center justify-center py-32">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-text mb-4">
              {error ? "Failed to load post" : "Post not found"}
            </h1>
            <Link
              href="/blog/"
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  return (
    <>
      {/* SEO: Dynamic meta tags and structured data */}
      {post && (
        <Head>
          <title>{post.title} | Specledger Blog</title>
          <meta name="description" content={post.excerpt} />
          <meta name="keywords" content={post.tags.join(", ")} />
          <link rel="canonical" href={`https://specledger.io/blog/?slug=${post.slug}`} />
          {/* Open Graph */}
          <meta property="og:title" content={post.title} />
          <meta property="og:description" content={post.excerpt} />
          <meta property="og:type" content="article" />
          <meta property="og:url" content={`https://specledger.io/blog/?slug=${post.slug}`} />
          <meta property="article:published_time" content={new Date(post.publishedAt).toISOString()} />
          {post.updatedAt && (
            <meta property="article:modified_time" content={new Date(post.updatedAt).toISOString()} />
          )}
          {post.author && <meta property="article:author" content={post.author} />}
          {post.tags.map((tag) => (
            <meta key={tag} property="article:tag" content={tag} />
          ))}
          {/* Twitter Card */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={post.title} />
          <meta name="twitter:description" content={post.excerpt} />
        </Head>
      )}

      {/* JSON-LD Structured Data */}
      {structuredData && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
        />
      )}

      <div className="min-h-screen bg-bg">
        <Navbar />

        {/* Hero Section */}
        <article>
          <header className="pt-24 md:pt-32 pb-8 md:pb-12 bg-gradient-to-b from-bg to-bg-alt">
            <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
              <AnimateOnScroll variant="fade-up">
                {/* Back Link */}
                <Link
                  href="/blog/"
                  className="inline-flex items-center gap-2 text-text-muted hover:text-primary transition-colors mb-6 sm:mb-8 group"
                >
                  <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" aria-hidden="true" />
                  <span className="text-sm">Back to Blog</span>
                </Link>

                {/* Title */}
                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-text mb-4 sm:mb-6 max-w-4xl tracking-tight break-words">
                  {post.title}
                </h1>

                {/* Meta */}
                <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-text-muted mb-6 sm:mb-8">
                  {post.author && (
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-primary" aria-hidden="true" />
                      <span className="text-sm" itemProp="author">{post.author}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-primary" aria-hidden="true" />
                    <time
                      dateTime={new Date(post.publishedAt).toISOString()}
                      className="text-sm"
                      itemProp="datePublished"
                    >
                      {formatDate(post.publishedAt)}
                    </time>
                  </div>
                  {post.readingTime && (
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-primary" aria-hidden="true" />
                      <span className="text-sm">{post.readingTime} min read</span>
                    </div>
                  )}
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-2" role="list" aria-label="Post tags">
                  {post.tags.map((tag) => (
                    <span
                      key={tag}
                      role="listitem"
                      className="px-2.5 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium bg-primary/10 text-primary"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </AnimateOnScroll>
            </div>
          </header>

          {/* Content Section with Two-Column Layout */}
          <section className="pb-16 sm:pb-20 bg-bg-alt">
            <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
                {/* Main Content */}
                <AnimateOnScroll variant="fade-up" delay={200} className="flex-1 min-w-0">
                  <div className="bg-white border border-border rounded-xl sm:rounded-2xl p-5 sm:p-6 md:p-8">
                    <BlogContentRenderer content={post.content} className="tiptap" />
                  </div>
                </AnimateOnScroll>

                {/* Sidebar - Table of Contents */}
                {sections.length > 0 && (
                  <AnimateOnScroll variant="fade-up" delay={300} className="hidden lg:block w-64 flex-shrink-0">
                    <aside className="sticky top-32">
                      <div className="bg-white border border-border rounded-xl p-5 sm:p-6 shadow-sm">
                        <TableOfContents
                          sections={sections}
                          activeSection={activeSection}
                          onSectionClick={handleSectionClick}
                        />
                      </div>
                    </aside>
                  </AnimateOnScroll>
                )}
              </div>
            </div>
          </section>
        </article>

        <Footer />
      </div>
    </>
  )
}
